import React, { useState } from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import FileUpload from '../../../../components/FileUpload/file-upload';
import { retryPayment } from '../../../../services/catalogService';
import { PaymentMethod } from '../../../../types/enums';

interface RetryPaymentModalProps {
  orderId: string;
  paymentMethod: PaymentMethod;
  onClose: () => void;
  onRetrySuccess: () => void;
}

const validationSchema = Yup.object({
  document: Yup.mixed().required('Document is required'),
});

const RetryPaymentModal: React.FC<RetryPaymentModalProps> = ({
  orderId,
  paymentMethod,
  onClose,
  onRetrySuccess,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('orderId', orderId);
      formData.append('paymentMethod', paymentMethod);
      formData.append('paymentProof', values.document);

      const response = await retryPayment(formData); // Replace with actual API call
      if (response?.isSuccess) {
        toast.success(response?.data?.message || t('retrySuccess', 'Payment retried successfully'));
        onRetrySuccess();
        onClose();
      } else {
        toast.error(response?.errorMessage || t('retryFailed', 'Retry failed'));
      }
    } catch (error: any) {
      toast.error(error.message || t('retryFailed', 'Retry failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-gray-600 bg-opacity-50">
      <div className="w-full max-w-3xl rounded-2xl bg-white p-8 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{t('retryPayment', 'Retry Payment')}</h2>
          <button className="text-xl text-gray-500" onClick={onClose}>
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/close.svg`}
              alt={t('close', 'close')}
            />
          </button>
        </div>

        <Formik
          initialValues={{ document: null }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue }) => (
            <Form>
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-[#344054]">
                  {t('uploadDocument', 'Upload Document')}
                  <span className="text-red-500">*</span>
                </label>
                <FileUpload
                  onFileSelect={(file: File | null) => setFieldValue('document', file)}
                  showUpload={false}
                />
                <ErrorMessage name="document" component="span" className="text-sm text-red-500" />
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  className="h-12 rounded-lg bg-primary px-12 py-2 font-semibold text-black"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t('submitting', 'Submitting...')
                    : t('retryPayment', 'Retry Payment')}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default RetryPaymentModal;
